import React from "react";
import { Navigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { Activity, RefreshCw } from "lucide-react";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import PageTitle from "@src/components/PageTitle";
import Spinner from "@src/components/Spinner";
import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderLeft,
  ButtonSection,
} from "../GuestCurator/GuestCurator.styles";
import { Subtitle } from "../SocialMediaPromo/SocialMediaPromo.styles";
import { useRole } from "@src/hooks/useAuth";
import { useTicketStatusCountsQuery } from "@src/queries/statusCheckQueries";
import { buildTicketStatusRows } from "@src/utils/statusCheck";
import {
  AdminBadge,
  CheckPanel,
  ContentWrapper,
  ErrorMessage,
  HeaderActions,
  IconBadge,
  PanelDescription,
  PanelHeader,
  PanelHeaderText,
  PanelTitle,
  QueryNote,
  RefreshButton,
  SectionLabel,
  StatusCard,
  StatusGrid,
  StatusName,
  StatusRawKey,
  StatusSection,
  StatusValue,
  TotalRow,
  TotalValue,
} from "./StatusCheck.styles";

const TICKET_STATUS_QUERY = `SELECT ticket_status, COUNT(*)
FROM tickets
GROUP BY ticket_status;`;

const getErrorMessage = (error: Error): string => {
  if (isAxiosError(error)) {
    if (error.response?.status === 403) {
      return "Your account is not allowed to read the ticket status counts.";
    }
    if (error.response?.status === 404) {
      return "The ticket status counts endpoint is not available on this environment yet.";
    }
  }
  return "Could not load the ticket status counts. Try again.";
};

const StatusCheckPage = (): React.JSX.Element => {
  const role = useRole();
  const { data, isPending, isFetching, isError, error, refetch } =
    useTicketStatusCountsQuery({ enabled: role === "admin" });

  if (role !== "admin") {
    return <Navigate to="/" />;
  }

  const counts = data?.data?.data;
  const rows = buildTicketStatusRows(counts?.counts);

  return (
    <WrapperContainer>
      <PageTopSection>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>Status Check</PageTitle>
              <Subtitle>
                Operational counts read straight from the backend.
              </Subtitle>
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>
        </PlaylistOverviewHeader>
      </PageTopSection>

      <ContentContainer allowScroll>
        <ContentWrapper>
          <CheckPanel>
            <PanelHeader>
              <IconBadge>
                <Activity size={26} />
              </IconBadge>
              <PanelHeaderText>
                <AdminBadge>Admin only</AdminBadge>
                <PanelTitle>Tickets by status</PanelTitle>
                <PanelDescription>
                  How many tickets sit in each status across every ticketed
                  event.
                </PanelDescription>
              </PanelHeaderText>
              <HeaderActions>
                <RefreshButton
                  type="button"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                >
                  <RefreshCw size={14} />
                  {isFetching ? "Refreshing…" : "Refresh"}
                </RefreshButton>
              </HeaderActions>
            </PanelHeader>

            <StatusSection>
              <SectionLabel>Breakdown</SectionLabel>

              {isPending && <Spinner />}

              {!isPending && isError && (
                <ErrorMessage>{getErrorMessage(error)}</ErrorMessage>
              )}

              {!isPending && !isError && (
                <>
                  <StatusGrid>
                    {rows.map((row) => (
                      <StatusCard
                        key={row.key}
                        $muted={row.known && row.count === 0}
                      >
                        <StatusName>{row.label}</StatusName>
                        <StatusValue>{row.count.toLocaleString()}</StatusValue>
                        {!row.known && (
                          <StatusRawKey>not in the status enum</StatusRawKey>
                        )}
                      </StatusCard>
                    ))}
                  </StatusGrid>

                  <TotalRow>
                    <SectionLabel>Total tickets</SectionLabel>
                    <TotalValue>
                      {(counts?.total ?? 0).toLocaleString()}
                    </TotalValue>
                  </TotalRow>
                </>
              )}
            </StatusSection>

            <StatusSection>
              <SectionLabel>Query</SectionLabel>
              <QueryNote>{TICKET_STATUS_QUERY}</QueryNote>
            </StatusSection>
          </CheckPanel>
        </ContentWrapper>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default StatusCheckPage;
